import React, { useMemo } from "react"
import { Box, BoxProps, Typography, useMediaQuery, useTheme } from "@carrotfertility/carotene-core"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarouselState
} from "#/features/ui/carousel"
import { ContentFilters, sprintsCarouselSections } from "#/features/sprints-landing/carousel-sections"
import Markdown from "markdown-to-jsx"
import { SPRINTS_MESSAGES } from "#/features/sprints-landing/en-only-text"

type SprintsCarouselOptions = {
  description: string
  image: string
  imageMobile: string
}

function SprintsCarouselDescription({ sections }: { sections: SprintsCarouselOptions[] }) {
  const { currentIndex } = useCarouselState()
  const currentOption = sections[currentIndex]

  return (
    <Typography variant="body1" sx={{ minBlockSize: { xs: "9rem", md: "6rem", lg: "4.5rem" } }}>
      <Markdown
        options={{
          overrides: {
            b: (props) => (
              <Typography component="span" fontWeight="bold" fontSize="inherit">
                {props.children}
              </Typography>
            )
          }
        }}
      >
        {currentOption?.description}
      </Markdown>
    </Typography>
  )
}

export function SprintsCarousel({ contentFilters }: { contentFilters: ContentFilters }) {
  const theme = useTheme()
  const showMobileImage = useMediaQuery(theme.breakpoints.down("sm"))
  const carouselSections = useMemo(
    () => sprintsCarouselSections.filter((section) => section.getShowContent(contentFilters)),
    [contentFilters]
  )
  return (
    <Carousel>
      <CarouselContent>
        {carouselSections.map((section) => (
          <CarouselItem key={section.id}>
            <SprintsCarouselImage src={showMobileImage ? section.imageMobile : section.image} />
          </CarouselItem>
        ))}
      </CarouselContent>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
          gap: theme.tokens.spacing.md
        })}
      >
        <CarouselPrevious aria-label={SPRINTS_MESSAGES.carousel.previousAriaLabel} />
        <SprintsCarouselDescription sections={carouselSections} />
        <CarouselNext aria-label={SPRINTS_MESSAGES.carousel.nextAriaLabel} />
      </Box>
    </Carousel>
  )
}

function SprintsCarouselImage(props: BoxProps<"img">) {
  return (
    <Box
      alt=""
      component="img"
      sx={{
        minBlockSize: 275,
        alignContent: "center",
        objectFit: "cover",
        inlineSize: "100%"
      }}
      {...props}
    />
  )
}
